/**
 * Platform timeline strip — BOTTOM_STRIP slot.
 *
 * Phase 13 P1 (June 2026)
 *
 * Bound on `has_timeline` capability, so every scene that authors
 * sequenced events shows the same timeline UX. Today the timeline
 * lives inline in `SceneDesignerPage`; the framework version
 * receives the same scene shape (via `useDesigner()`) and exposes
 * the same `selectEvent` / `mutateScene` API.
 *
 * The strip itself is a horizontal list of event chips; clicking
 * a chip selects the event (which the EventInspector then renders
 * in the right rail). Mutating durations is a side-effect-free
 * tweak to the event's `duration_ms`.
 */
import React from 'react';

import { DesignerTool } from '../DesignerTool';
import { useDesigner } from '../DesignerContext';
import { requiresCapability } from '../predicates';

function TimelineStrip() {
  const {
    scene, selectedEventIndex, selectEvent, mutateScene,
  } = useDesigner();
  if (!scene) return null;
  const events = scene.timeline || [];

  const insertEvent = () => {
    const ev = {
      event_id:   `e_${events.length + 1}_${Date.now().toString(36)}`,
      duration_ms: 1000,
      coaching_script: '',
    };
    mutateScene({ ...scene, timeline: [...events, ev] });
    selectEvent(events.length);
  };

  const removeEvent = (idx) => {
    const next = events.filter((_, i) => i !== idx);
    mutateScene({ ...scene, timeline: next });
    if (selectedEventIndex >= next.length) selectEvent(Math.max(0, next.length - 1));
  };

  return (
    <div
      className="designer-tool designer-tool--timeline"
      data-tool-id="platform.timeline_strip"
      role="tablist"
      aria-label="Scene timeline"
    >
      {events.length === 0 && (
        <span className="designer-tool__empty">No timeline events yet.</span>
      )}
      {events.map((ev, i) => (
        <button
          key={ev.event_id || i}
          type="button"
          className={
            'designer-tool__chip designer-tool__chip--event' +
            (i === selectedEventIndex ? ' is-active' : '')
          }
          role="tab"
          aria-selected={i === selectedEventIndex}
          aria-label={`Timeline event ${i + 1}: ${ev.event_id || 'unnamed'}`}
          title={`${ev.event_id || `event-${i + 1}`} (${ev.duration_ms || 0} ms)`}
          onClick={() => selectEvent(i)}
        >
          <span className="designer-tool__chip-label">{ev.event_id || `e${i + 1}`}</span>
          <span className="designer-tool__chip-meta">{ev.duration_ms || 0} ms</span>
          <span
            className="designer-tool__chip-close"
            role="button"
            tabIndex={-1}
            aria-label={`Remove event ${i + 1}`}
            onClick={(e) => { e.stopPropagation(); removeEvent(i); }}
          >×</span>
        </button>
      ))}
      <button
        type="button"
        className="designer-tool__button designer-tool__button--small"
        aria-label="Append timeline event"
        onClick={insertEvent}
      >
        + Event
      </button>
    </div>
  );
}

export const timelineStrip = new DesignerTool({
  id:         'platform.timeline_strip',
  label:      'Timeline',
  slot:       'BOTTOM_STRIP',
  category:   'timeline',
  applies_to: requiresCapability('has_timeline'),
  Component:  TimelineStrip,
  weight:     50,
  helpText:   'Sequence pre-snap, snap, and post-snap events.',
});

export default timelineStrip;
